import React, { useState, useMemo, useCallback } from "react";
import {
  Combobox,
  TextInput,
  useCombobox,
  CloseButton,
  Text,
  Group,
  Avatar,
  Box,
  ScrollArea,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { VehicleMake } from "@/utils/api";
import { useAppEnvironment, useAppEssentialsContext } from "@/contexts";
import { LoadingIndicator, AppIcon } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { VehicleMakePickerModal } from "./VehicleMakePickerModal";

// ============================================
// Types
// ============================================

export interface VehicleMakeSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected vehicle make ID */
  value?: number | string | null | undefined;

  /** Callback fired when vehicle make selection changes: (name, value, make?) */
  onChange?: (name: string, value: number | null, make?: VehicleMake) => void;

  /** Placeholder text when no make is selected */
  placeholder?: string;

  /** Custom filter function to filter vehicle makes */
  makeFilter?: (make: VehicleMake) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Whether to show logo/avatar in options */
  showLogo?: boolean;
}

// ============================================
// Constants
// ============================================

const ICON_SIZES = {
  inputLeft: 20,
  inputRight: 18,
  option: 24,
  optionIcon: 14,
};

const FUSE_OPTIONS: IFuseOptions<VehicleMake> = {
  keys: [
    { name: "makeName", weight: 0.8 },
    { name: "code", weight: 0.2 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 1,
  ignoreLocation: true,
};

// ============================================
// Component
// ============================================

const VehicleMakeSelectorComponent: React.FC<VehicleMakeSelectorProps> = ({
  name = "makeId",
  value,
  onChange,
  placeholder,
  makeFilter,
  required = false,
  error,
  disabled,
  size = "md",
  label,
  showLogo = true,
}) => {
  const { t } = useTranslation(["cars", "common"]);
  const { appEssentials, isLoading, lastError, getVehicleMakeById } =
    useAppEssentialsContext();
  const { isMobile } = useAppEnvironment();

  const vehicleMakes = appEssentials?.vehicleMakes || [];

  const [modalOpened, setModalOpened] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearchValue("");
    },
  });

  // Default translations
  const defaultPlaceholder =
    placeholder || t("cars:selectors.vehicleMake.placeholder");
  const defaultLabel = label ?? t("cars:selectors.vehicleMake.label");

  // Create a map for quick vehicle make lookup by ID
  const vehicleMakeMap = useMemo(() => {
    const map = new Map<number, VehicleMake>();
    vehicleMakes.forEach((make) => {
      if (make.id !== undefined) {
        map.set(make.id, make);
      }
    });
    return map;
  }, [vehicleMakes]);

  // Apply custom filter if provided, then sort alphabetically
  const sortedMakes = useMemo(() => {
    let filtered = vehicleMakes;
    if (makeFilter) {
      filtered = filtered.filter(makeFilter);
    }
    return [...filtered].sort((a, b) =>
      (a.makeName || "").localeCompare(b.makeName || "")
    );
  }, [vehicleMakes, makeFilter]);

  // Build Fuse index for fuzzy search
  const fuse = useMemo(() => new Fuse(sortedMakes, FUSE_OPTIONS), [sortedMakes]);

  // Filter makes based on search value
  const filteredMakes = useMemo(() => {
    const search = searchValue.trim();
    if (!search) return sortedMakes;
    return fuse.search(search).map((result) => result.item);
  }, [sortedMakes, searchValue, fuse]);

  // Get selected vehicle make for display
  const selectedMake = useMemo(() => {
    if (value === null || value === undefined) return null;
    const numericValue =
      typeof value === "string" ? parseInt(value, 10) : value;
    return vehicleMakeMap.get(numericValue) || getVehicleMakeById(numericValue);
  }, [value, vehicleMakeMap, getVehicleMakeById]);

  // Handle selection (desktop combobox)
  const handleSelect = useCallback(
    (makeIdStr: string) => {
      const numericId = parseInt(makeIdStr, 10);
      if (isNaN(numericId)) {
        onChange?.(name, null, undefined);
      } else {
        const make =
          vehicleMakeMap.get(numericId) || getVehicleMakeById(numericId);
        onChange?.(name, numericId, make);
      }
      combobox.closeDropdown();
    },
    [name, vehicleMakeMap, getVehicleMakeById, onChange, combobox]
  );

  // Handle modal selection
  const handleModalSelect = useCallback(
    (makeId: number | null, make?: VehicleMake) => {
      onChange?.(name, makeId, make);
    },
    [name, onChange]
  );

  // Handle clear selection
  const handleClear = useCallback(() => {
    onChange?.(name, null, undefined);
    setSearchValue("");
  }, [name, onChange]);

  // Handle clearing search text (without deselecting the make)
  const handleClearSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.currentTarget.value);
      combobox.updateSelectedOptionIndex();
    },
    [combobox]
  );

  // Display value in input
  const displayValue = useMemo(() => {
    if (!isMobile && combobox.dropdownOpened) {
      return searchValue;
    }
    return selectedMake?.makeName || "";
  }, [isMobile, combobox.dropdownOpened, searchValue, selectedMake]);

  // Left section icon/avatar
  const leftSection = useMemo(() => {
    if (!showLogo || !selectedMake || (!isMobile && combobox.dropdownOpened)) {
      return <AppIcon iconName="Car" size={ICON_SIZES.inputLeft} />;
    }
    return <AppIcon iconName="Car" size={ICON_SIZES.inputLeft} />;
  }, [showLogo, selectedMake, isMobile, combobox.dropdownOpened]);

  // Determine disabled state
  const isDisabled =
    disabled || (isLoading && vehicleMakes.length === 0);

  // Determine placeholder
  const currentPlaceholder = useMemo(() => {
    if (isLoading && vehicleMakes.length === 0) {
      return t("cars:selectors.vehicleMake.loading");
    }
    if (lastError && !isLoading && vehicleMakes.length === 0) {
      return t("common:errors.generic");
    }
    if (vehicleMakes.length === 0) {
      return t("cars:selectors.vehicleMake.empty");
    }
    return defaultPlaceholder;
  }, [isLoading, vehicleMakes.length, lastError, defaultPlaceholder, t]);

  // Error state
  const displayError = useMemo(() => {
    if (lastError && !isLoading && vehicleMakes.length === 0) {
      return error || t("common:messages.loadError");
    }
    return error;
  }, [lastError, isLoading, vehicleMakes.length, error, t]);

  // Right section content (desktop)
  const getRightSection = () => {
    if (isLoading && vehicleMakes.length === 0) {
      return <LoadingIndicator size={size} color="gray" />;
    }

    if (combobox.dropdownOpened && searchValue) {
      return (
        <CloseButton
          size={size}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClearSearch}
          aria-label={t("common:form.clearSearch")}
        />
      );
    }

    if (selectedMake && !required && !disabled && !combobox.dropdownOpened) {
      return (
        <CloseButton
          size={size}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
          aria-label={t("common:form.clearSelection")}
        />
      );
    }

    return <AppIcon iconName="ChevronDown" size={ICON_SIZES.inputRight} />;
  };

  // Right section for mobile trigger input
  const getMobileRightSection = () => {
    if (isLoading && vehicleMakes.length === 0) {
      return <LoadingIndicator size={size} color="gray" />;
    }

    if (selectedMake && !required && !isDisabled) {
      return (
        <CloseButton
          size={size}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            handleClear();
          }}
          aria-label={t("common:form.clearSelection")}
        />
      );
    }

    return <AppIcon iconName="ChevronDown" size={ICON_SIZES.inputRight} />;
  };

  // ==========================================
  // Mobile: Read-only input + full-screen modal
  // ==========================================
  if (isMobile) {
    return (
      <Box>
        {/* Trigger Input — read-only, opens modal on tap */}
        <TextInput
          label={defaultLabel}
          placeholder={currentPlaceholder}
          value={selectedMake?.makeName || ""}
          readOnly
          onClick={() => {
            if (!isDisabled) {
              setModalOpened(true);
            }
          }}
          rightSection={getMobileRightSection()}
          rightSectionPointerEvents={
            isLoading && vehicleMakes.length === 0
              ? "none"
              : selectedMake && !required && !isDisabled
                ? "auto"
                : "none"
          }
          error={displayError}
          required={required}
          disabled={isDisabled}
          size={size}
          leftSection={leftSection}
          styles={{
            input: {
              cursor: isDisabled ? "not-allowed" : "pointer",
              caretColor: "transparent",
            },
          }}
        />

        {/* Full-screen Picker Modal */}
        <VehicleMakePickerModal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          value={
            value !== null && value !== undefined
              ? typeof value === "string"
                ? parseInt(value, 10)
                : value
              : null
          }
          onSelect={handleModalSelect}
          makeFilter={makeFilter}
          showLogo={showLogo}
        />
      </Box>
    );
  }

  // ==========================================
  // Desktop: Original Combobox behavior
  // ==========================================
  return (
    <Box>
      <Combobox
        store={combobox}
        onOptionSubmit={handleSelect}
        withinPortal={DRAWER_COMBOBOX_PROPS.withinPortal}
        zIndex={DRAWER_COMBOBOX_PROPS.zIndex}
        position={DRAWER_COMBOBOX_PROPS.position}
        middlewares={DRAWER_COMBOBOX_PROPS.middlewares}
      >
        <Combobox.Target>
          <TextInput
            label={defaultLabel}
            placeholder={currentPlaceholder}
            value={displayValue}
            onChange={handleSearchChange}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => {
              combobox.closeDropdown();
              setSearchValue("");
            }}
            rightSection={getRightSection()}
            rightSectionPointerEvents={
              (combobox.dropdownOpened && searchValue) ||
                (selectedMake && !required)
                ? "auto"
                : "none"
            }
            error={displayError}
            required={required}
            disabled={isDisabled}
            size={size}
            leftSection={leftSection}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <ScrollArea.Autosize mah={300} type="scroll">
            <Combobox.Options>
              {/* No results message */}
              {filteredMakes.length === 0 && searchValue && (
                <Combobox.Empty>
                  {t("common:form.noOptions")}
                </Combobox.Empty>
              )}

              {/* Vehicle make options */}
              {filteredMakes.map((make) => {
                const numericValue =
                  value !== null && value !== undefined
                    ? typeof value === "string"
                      ? parseInt(value, 10)
                      : value
                    : null;

                return (
                  <Combobox.Option
                    key={make.id}
                    value={String(make.id)}
                    active={numericValue === make.id}
                    py="sm"
                  >
                    <Group gap="sm" wrap="nowrap">
                      {showLogo ? (
                        <Avatar size={ICON_SIZES.option} radius="sm" color="gray">
                          <AppIcon iconName="Car" size={ICON_SIZES.optionIcon} />
                        </Avatar>
                      ) : (
                        <AppIcon iconName="Car" size={ICON_SIZES.inputLeft} />
                      )}
                      <Text size="md" fw={500}>
                        {make.makeName}
                      </Text>
                    </Group>
                  </Combobox.Option>
                );
              })}
            </Combobox.Options>
          </ScrollArea.Autosize>
        </Combobox.Dropdown>
      </Combobox>
    </Box>
  );
};

export const VehicleMakeSelector = React.memo(VehicleMakeSelectorComponent);